import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Printer, FileText, UserX } from "lucide-react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { printDecision, decisionPDFBlob } from "../lib/decisionPrint";
import { WaSendButton } from "./WaSendButton";

// Saudi employees active in WorkZone but missing from the uploaded GOSI active sheet.
export const MissingDialog = ({ list, onClose, onDone }) => {
  const [sel, setSel] = useState(() => new Set(list.map((e) => e.id)));
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.saudiMarkLeft([...sel]);
      toast.success(`تم تحويل ${r.marked} موظف إلى غير نشط وإصدار خطاب قبول استقالة لكل منهم`, { duration: 8000 });
      onDone(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التنفيذ"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="saudi-missing-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserX className="h-5 w-5 text-rose-600" /> موظفون غير موجودين في شيت التأمينات ({list.length})</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">هؤلاء نشطون في النظام لكن غير موجودين في شيت المشتركين النشطين. حدّد من تريد تحويله إلى غير نشط — السبب «استقالة»، وتاريخ المغادرة يُؤخذ من شيت غير النشطين عند رفعه (مؤقتاً تاريخ اليوم).</p>
        <div className="flex items-center justify-between text-xs">
          <button onClick={() => setSel(new Set(sel.size === list.length ? [] : list.map((e) => e.id)))} className="font-semibold text-blue-700" data-testid="saudi-missing-all">{sel.size === list.length ? "إلغاء تحديد الكل" : "تحديد الكل"}</button>
          <span className="text-slate-500">محدد: {sel.size}</span>
        </div>
        <div className="max-h-[50vh] space-y-1.5 overflow-y-auto">
          {list.map((e) => (
            <label key={e.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 hover:bg-slate-50" data-testid={`saudi-missing-${e.id}`}>
              <Checkbox checked={sel.has(e.id)} onCheckedChange={() => toggle(e.id)} />
              <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold">{e.name}</p>
                <p className="text-[11px] text-slate-500">{[e.iqama_number, e.job_title, e.hire_date && `التحق ${e.hire_date}`].filter(Boolean).join(" · ")}</p></div>
            </label>))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>تجاهل</Button>
          <Button onClick={confirm} disabled={busy || !sel.size} className="gap-1.5 bg-rose-600 hover:bg-rose-700" data-testid="saudi-missing-confirm">{busy && <Loader2 className="h-4 w-4 animate-spin" />} تأكيد التحويل وإصدار الخطابات</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ExitLetter = ({ e, canEdit }) => {
  const { company } = useAuth();
  const d = e.exit_letter;
  if (!d) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2" data-testid={`saudi-letter-${e.id}`}>
      <FileText className="h-4 w-4 text-rose-600" />
      <span className="flex-1 text-xs font-semibold text-rose-800">{d.type} رقم {d.number}{e.leave_date_pending && <span className="mr-2 font-normal text-amber-700">(تاريخ المغادرة مؤقت — يُحدَّث عند رفع شيت غير النشطين)</span>}</span>
      <Button size="sm" variant="secondary" className="h-7 gap-1 text-xs" onClick={() => printDecision(d, company, fileUrl)} data-testid={`saudi-letter-print-${e.id}`}><Printer className="h-3.5 w-3.5" /> عرض / PDF</Button>
      {canEdit && <WaSendButton employeeId={e.id} employeeName={e.name} refType="decision" refId={d.id} testid={`saudi-letter-wa-${e.id}`} emailTitle={d.title || d.type || "خطاب"}
        pdf={async () => ({ blob: await decisionPDFBlob(d, company, fileUrl), filename: `${d.type}-${String(d.number).replace(/\//g, "-")}.pdf` })}
        text={`السلام عليكم ${e.name}\nمرفق ${d.type} رقم ${d.number}.`} />}
    </div>
  );
};
